import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';

export interface SessionUser {
  id: string;
  email: string | null;
  name: string | null;
  tenant_id: string | null;
}

export const useAdminSessionsService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  const authHeader = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const listUsers = useCallback(async (): Promise<SessionUser[]> => {
    const headers = await authHeader();
    const resp = await fetch(`${apiUrl}/api/admin/sessions/users`, { headers });
    if (!resp.ok) throw new Error('Failed to list users');
    return resp.json();
  }, [apiUrl, authHeader]);

  const disconnectUser = useCallback(async (userId: string): Promise<void> => {
    const headers = await authHeader();
    const resp = await fetch(
      `${apiUrl}/api/admin/sessions/users/${encodeURIComponent(userId)}/disconnect`,
      { method: 'POST', headers },
    );
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(body.message || body.detail || `Disconnect failed (${resp.status})`);
    }
  }, [apiUrl, authHeader]);

  const disconnectAll = useCallback(async (): Promise<void> => {
    const headers = await authHeader();
    const resp = await fetch(`${apiUrl}/api/admin/sessions/disconnect-all`, {
      method: 'POST',
      headers,
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(body.message || body.detail || `Disconnect-all failed (${resp.status})`);
    }
  }, [apiUrl, authHeader]);

  return { listUsers, disconnectUser, disconnectAll };
};
